import { Capacitor } from '@capacitor/core';
import { CapacitorInAppPurchase } from '@adplorg/capacitor-in-app-purchase';

/**
 * Apple App Store product IDs for In-App Purchase.
 * Must match App Store Connect exactly.
 */
export const IAP_PRODUCTS = {
  fullAnalysis: 'app.quilty.full_analysis_49_99',
};

export function useNativeIAP(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

export async function initIAP(): Promise<void> {
  if (!useNativeIAP()) return;

  try {
    await CapacitorInAppPurchase.getProducts({
      productIds: Object.values(IAP_PRODUCTS),
    });
  } catch (e) {
    if (import.meta.env.DEV) {
      console.error('IAP cache warm-up failed (non-fatal):', e);
    }
  }
}

export async function purchaseFullAnalysis(): Promise<{
  success: boolean;
  transactionId?: string;
  receipt?: string;
  error?: string;
  productNotFound?: boolean;
}> {
  if (!useNativeIAP()) {
    return { success: false, error: 'IAP not available on this platform' };
  }

  try {
    const referenceUUID = crypto.randomUUID();

    const result = await CapacitorInAppPurchase.purchaseProduct({
      productId: IAP_PRODUCTS.fullAnalysis,
      referenceUUID,
    });

    const transactionJson: string = result.transaction;
    if (!transactionJson) {
      return { success: false, error: 'Purchase failed — no transaction data' };
    }

    const transaction = JSON.parse(transactionJson) as { id?: string | number };
    const transactionId = String(transaction.id ?? '');

    if (!transactionId) {
      return { success: false, error: 'Purchase failed — missing transaction ID' };
    }

    return {
      success: true,
      transactionId,
      receipt: transactionJson,
    };
  } catch (e: unknown) {
    const err = e as { message?: string; code?: number };
    if (
      err?.message?.toLowerCase().includes('cancelled by user') ||
      err?.code === -2
    ) {
      return { success: false };
    }
    if (
      err?.message?.toLowerCase().includes('product not found') ||
      err?.code === -1
    ) {
      return { success: false, productNotFound: true };
    }
    return {
      success: false,
      error: err?.message || 'Purchase error',
    };
  }
}

export async function restorePurchases(): Promise<{
  success: boolean;
  transactions?: unknown[];
  error?: string;
}> {
  if (!useNativeIAP()) {
    return { success: false, error: 'IAP not available on this platform' };
  }

  try {
    const result = await CapacitorInAppPurchase.getActiveSubscriptions();
    return {
      success: true,
      transactions: result.subscriptions,
    };
  } catch (e: unknown) {
    const err = e as { message?: string };
    return {
      success: false,
      error: err?.message || 'Restore failed',
    };
  }
}

export async function getProducts(): Promise<unknown[]> {
  if (!useNativeIAP()) {
    return [];
  }

  try {
    const result = await CapacitorInAppPurchase.getProducts({
      productIds: Object.values(IAP_PRODUCTS),
    });
    return result.products || [];
  } catch (e) {
    if (import.meta.env.DEV) {
      console.error('Failed to get products:', e);
    }
    return [];
  }
}
